import { useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useAppStore from "./microsite-store";

/**
 * Custom hook for handling geolocation functionality
 *
 * @example
 * const {
 *   locating,
 *   error,
 *   fetchGeolocation,
 *   fetchLocationsFromGeo
 * } = useGeolocation();
 *
 * @returns {Object} Geolocation state and methods
 */
export function useGeolocation() {
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  const { t } = useTranslation();
  const {
    campaignId,
    entityInfo,
    setCoords,
    setLocation,
    setCoordsNoLocation,
  } = useAppStore((state) => ({
    campaignId: state.campaignId,
    entityInfo: state.entityInfo,
    setCoords: state.setCoords,
    setLocation: state.setLocation,
    setCoordsNoLocation: state.setCoordsNoLocation,
  }));

  /**
   * Check and set location from response data
   * @param {Object} entityInfoData - Entity info data containing location info
   */
  const checkAndSetLocationFromResponse = useCallback(
    (entityInfoData) => {
      if (!entityInfoData.location_locked && entityInfoData.location) {
        setLocation(entityInfoData.location);
      }
      setLocating(false);
    },
    [setLocation]
  );

  /**
   * Fetch locations from geolocation coordinates
   * @param {Object} position - Position object with latitude and longitude
   * @param {Object} entityInfoData - Entity info data for location validation
   */
  const fetchLocationsFromGeo = useCallback(
    (position, entityInfoData) => {
      if (!position) {
        if (entityInfoData.location_locked) {
          setError(t("no_coords_error"));
          setLocating(false);
        } else {
          checkAndSetLocationFromResponse(entityInfoData);
        }
        return;
      }

      const req = {
        campaign_id: campaignId,
        campaign_effort_id: entityInfo.campaign_effort_id,
        latitude: position.latitude,
        longitude: position.longitude,
      };

      if (entityInfo && entityInfo.location) {
        req.location_id = entityInfo.location?.id;
      }

      axios
        .post(`/microsite/location-point-check/`, req)
        .then(({ data }) => {
          if (!data.response.length) {
            if (entityInfoData.location_locked) {
              setLocating(false);
              setError(t("not_at_location_error"));
            } else {
              checkAndSetLocationFromResponse(entityInfoData);
            }
          } else {
            const foundGeospot = data.response.find((f) => f.geo_spot_id);
            const foundLocation = data.response.find(
              (f) => f.class_name === "Location"
            );

            if (foundLocation) {
              if (
                entityInfoData.location &&
                entityInfoData.location_locked &&
                entityInfoData.location.id !== foundLocation.id
              ) {
                setLocating(false);
                setError(t("not_at_location_error"));
              } else {
                setLocation({
                  ...foundLocation,
                  present_at_location: true,
                });
                setLocating(false);
              }
            } else {
              setLocation(foundGeospot);
              setLocating(false);
            }
          }
        })
        .catch((err) => {
          setError(err);
          setLocating(false);
        });
    },
    [campaignId, entityInfo, setLocation, checkAndSetLocationFromResponse, t]
  );

  /**
   * Fetch geolocation from browser
   * @param {Object} entityInfoData - Entity info data for location validation
   */
  const fetchGeolocation = useCallback(
    (entityInfoData) => {
      setLocating(true);
      setError(null);

      const successCallback = (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCoords({
          latitude: lat,
          longitude: lng,
        });
        fetchLocationsFromGeo(
          {
            latitude: lat,
            longitude: lng,
          },
          entityInfoData
        );
      };

      const errorCallback = (error) => {
        // if location required, set error
        if (entityInfoData.location_locked) {
          setLocating(false);
          return setError(t("location_services_required_error"));
        } else {
          checkAndSetLocationFromResponse(entityInfoData);
          setLocating(false);
          setCoords(null);
        }
      };

      navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: Infinity,
      });
    },
    [setCoords, fetchLocationsFromGeo, checkAndSetLocationFromResponse, t]
  );

  return {
    locating,
    error,
    fetchGeolocation,
    fetchLocationsFromGeo,
    checkAndSetLocationFromResponse,
    setLocating,
    setError,
  };
}
